import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Download, Heart, TrendingUp, Star, Wand2, X, ChevronDown } from 'lucide-react';
import { AppIcon, CountryCode, COUNTRIES } from '../types';
import { IconCard } from './IconCard';
import { DownloadService } from '../services/download';
import { iTunesService } from '../services/api';
import { useAppStore } from '../stores/appStore';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';

interface SearchBarProps {
  onSearch: (query: string, country: CountryCode) => void;
  onCountryChange: (country: CountryCode) => void;
  currentCountry: CountryCode;
  searchQuery: string;
}

const SearchBar: React.FC<SearchBarProps> = ({ 
  onSearch, 
  onCountryChange, 
  currentCountry, 
  searchQuery 
}) => {
  const [query, setQuery] = useState(searchQuery);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setQuery(searchQuery);
  }, [searchQuery]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      onSearch(query.trim(), currentCountry);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
      <form onSubmit={handleSubmit}>
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <div className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center">
              <button
                type="button"
                onClick={() => setOpen(!open)}
                className="flex items-center px-2 py-1.5 rounded-full bg-transparent text-gray-800"
                aria-haspopup="listbox"
                aria-expanded={open}
              >
                <span className="text-xs font-mono text-purple-700">{currentCountry.toUpperCase()}</span>
                <ChevronDown className="w-4 h-4 ml-0.5 text-purple-600" />
              </button>
              <span className="mx-1.5 h-5 w-px bg-gray-300" />
              <Search className="text-purple-500 w-4 h-4" />
            </div>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search app icons..."
              className="w-full pl-28 pr-4 py-3 border border-purple-400 rounded-full focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all"
            />
            {open && (
              <div className="absolute left-3 top-full z-20 mt-2 w-20 bg-white border border-gray-200 rounded-lg shadow-lg">
                {Object.keys(COUNTRIES).map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => { onCountryChange(code as CountryCode); setOpen(false); }}
                    className={`flex w-full items-center px-3 py-2 text-sm hover:bg-purple-50 ${code === currentCountry ? 'text-purple-700' : 'text-gray-700'}`}
                  >
                    <span className="font-mono">{(code as string).toUpperCase()}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={!query.trim()}
            className="px-6 py-3 bg-purple-600 text-white rounded-full hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            Search
          </button>
        </div>
      </form>
    </div>
  );
};

// 移除筛选条件模块

interface SearchResultsProps {
  results: AppIcon[];
  isLoading: boolean;
  query: string;
  country: CountryCode;
  onDownload: (icon: AppIcon) => void;
}

const SearchResults: React.FC<SearchResultsProps> = ({ results, isLoading, query, country, onDownload }) => {
  const navigate = useNavigate();
  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 10 }).map((_, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 animate-pulse h-28"></div>
        ))}
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="text-center py-12">
        <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No icons found</h3>
        <p className="text-gray-500">Try different keywords</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <h3 className="text-lg font-semibold text-gray-900">Search Results</h3>
          <span className="text-sm text-gray-500">{results.length} apps</span>
        </div>
        <div className="text-sm text-gray-500">
          Query: "{query}"
        </div>
      </div>

      <div className="space-y-4">
        {results.map((icon) => (
          <div key={icon.trackId} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-start">
              {/* 左侧缩略图 */}
              <div className="w-16 h-16 rounded-lg bg-gray-50 flex items-center justify-center overflow-hidden mr-4">
                <img src={icon.artworkUrl512} alt={icon.trackName} className="w-full h-full object-contain" />
              </div>

              {/* 中间信息区 */}
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-base font-semibold text-gray-900">{icon.trackName}</h4>
                    <p className="text-sm text-gray-600">{icon.artistName} ・ {icon.primaryGenreName || 'App'}</p>
                  </div>
                </div>

                <div className="mt-2 flex items-center flex-wrap gap-3 text-sm">
                  {icon.averageUserRating && (
                    <span className="inline-flex items-center text-gray-700">
                      <Star className="w-4 h-4 text-yellow-500 mr-1" />
                      {icon.averageUserRating.toFixed(1)}{icon.userRatingCount ? ` (${icon.userRatingCount.toLocaleString()})` : ''}
                    </span>
                  )}
                  <span className="text-green-600 font-medium">{icon.formattedPrice || (icon.price && icon.price > 0 ? `${icon.price}` : 'Free')}</span>
                  <span className="text-gray-500">Icon Sizes:</span>
                  <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded cursor-default select-none">60px</span>
                  <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded cursor-default select-none">100px</span>
                  <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded cursor-default select-none">512px</span>
                </div>
              </div>

              {/* 右侧操作区 */}
              <div className="ml-4 flex items-center space-x-2">
                <button title="AI Edit" onClick={() => navigate('/ai-editor', { state: { icon } })} className="p-2 rounded hover:bg-purple-50 text-purple-600">
                  <Wand2 className="w-5 h-5" />
                </button>
                <button title="Download" onClick={() => onDownload(icon)} className="p-2 rounded hover:bg-gray-50 text-gray-600">
                  <Download className="w-5 h-5" />
                </button>
                <button title="Favorite" className="p-2 rounded hover:bg-gray-50 text-gray-600">
                  <Heart className="w-5 h-5" />
                </button>
                {/* 复制链接按钮已移除 */}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface HotIconsProps {
  onAIEdit: (icon: AppIcon) => void;
}

const HotIcons: React.FC<HotIconsProps> = ({ onAIEdit }) => {
  const { currentCountry } = useAppStore();
  const [hotIcons, setHotIcons] = useState<AppIcon[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadHotIcons();
  }, [currentCountry]);

  const loadHotIcons = async () => {
    setIsLoading(true);
    try {
      const response = await iTunesService.getTopIcons(currentCountry, 10);
      if (response.success && response.data) {
        setHotIcons(response.data.results);
      }
    } catch (error) {
      console.error('加载热门图标失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <TrendingUp className="w-5 h-5 text-purple-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Trending Icons</h3>
        </div>
        <span className="text-sm text-gray-500">
          {COUNTRIES[currentCountry].flag} {COUNTRIES[currentCountry].name}
        </span>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Array.from({ length: 10 }).map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="aspect-square bg-gray-200 rounded-lg mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {hotIcons.map((icon) => (
            <div key={icon.trackId} className="group cursor-pointer">
              <div className="aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-3 mb-2 group-hover:shadow-md transition-shadow">
                <img
                  src={icon.artworkUrl512}
                  alt={icon.trackName}
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>
              <h4 className="text-sm font-medium text-gray-900 line-clamp-1" title={icon.trackName}>
                {icon.trackName}
              </h4>
              <p className="text-xs text-gray-500 line-clamp-1" title={icon.artistName}>
                {icon.artistName}
              </p>
              <div className="mt-2 flex space-x-1">
                <button
                  onClick={() => onAIEdit(icon)}
                  className="flex-1 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors"
                >
                  AI Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const IconSearch: React.FC = () => {
  const { 
    currentCountry, 
    searchQuery, 
    searchResults, 
    isLoading, 
    setSearchQuery, 
    setSearchResults, 
    setLoading, 
    setError,
    clearError,
  } = useAppStore();
  const { settings } = useAppStore();

  const HOT_KEYWORDS = ['WeChat', 'QQ', 'Instagram', 'TikTok', 'YouTube', 'Twitter'];

  const handleSearch = async (query: string, country: CountryCode) => {
    if (!query.trim()) return;

    setLoading(true);
    clearError();
    setSearchQuery(query);

    try {
      const response = await iTunesService.searchIcons({
        term: query,
        country: country,
        limit: 50,
      });

      if (response.success && response.data) {
        setSearchResults(response.data.results);
      } else {
        setError(response.error || '搜索失败');
        toast.error(response.error || '搜索失败');
      }
    } catch (error) {
      const errorMessage = '搜索过程中发生错误';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCountryChange = (country: CountryCode) => {
    useAppStore.getState().setCurrentCountry(country);
  };

  const handleFilterChange = (filters: any) => {
    // 这里可以实现筛选逻辑
    console.log('筛选条件:', filters);
  };

  const handleHotSearch = (keyword: string) => {
    setSearchQuery(keyword);
    handleSearch(keyword, currentCountry);
  };

  const [downloadIcon, setDownloadIcon] = useState<AppIcon | null>(null);
  const SIZE_CHOICES = [
    { value: 16, label: '16×16 - Small' },
    { value: 32, label: '32×32 - Standard' },
    { value: 64, label: '64×64 - Medium' },
    { value: 128, label: '128×128 - Large' },
    { value: 256, label: '256×256 - HD' },
    { value: 512, label: '512×512 - UHD' },
    { value: 1024, label: '1024×1024 - Max' },
  ];
  const [options, setOptions] = useState<Array<{ format: 'png' | 'jpeg' | 'webp' | 'ico' | 'svg'; size: number }>>([
    { format: 'png', size: 512 },
  ]);
  const [quality, setQuality] = useState<number>(settings.downloadQuality);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const addOption = () => setOptions([...options, { format: 'png', size: 256 }]);
  const removeOption = (idx: number) => setOptions(options.filter((_, i) => i !== idx));
  const updateOptionFormat = (idx: number, f: 'png' | 'jpeg' | 'webp' | 'ico' | 'svg') => setOptions(options.map((o, i) => i === idx ? { ...o, format: f } : o));
  const updateOptionSize = (idx: number, s: number) => setOptions(options.map((o, i) => i === idx ? { ...o, size: s } : o));
  const applyQuickPNGAll = () => setOptions([16,32,64,128,256,512].map(sz => ({ format: 'png', size: sz })));
  const applyQuickICOCommon = () => setOptions([16,32,64].map(sz => ({ format: 'ico', size: sz })));

  const startDownload = async () => {
    if (!downloadIcon) return;
    try {
      const svc = DownloadService.getInstance();
      for (const opt of options) {
        await svc.downloadImage(
          downloadIcon.artworkUrl512,
          `${downloadIcon.trackName}-${opt.size}x${opt.size}`,
          {
            format: opt.format,
            quality,
            size: { width: opt.size, height: opt.size },
          }
        );
      }
      setDownloadIcon(null);
    } catch (e) {
      setDownloadIcon(null);
    }
  };

  return (
    <div className="w-full md:w-3/5 mx-auto px-0 py-8">
      <Helmet>
        <title>{searchQuery ? `Search "${searchQuery}" – Icon Search AI` : 'Search app icons – Icon Search AI'}</title>
        <meta name="description" content={searchQuery ? `Find app icons for "${searchQuery}" and download in SVG/PNG. Edit with AI.` : 'Search App Store icons and download in multiple formats. Edit with AI and organize favorites.'} />
      </Helmet>
      <SearchBar
        onSearch={handleSearch}
        onCountryChange={handleCountryChange}
        currentCountry={currentCountry}
        searchQuery={searchQuery}
      />

      {/* 热门搜索关键词 */}
      <div className="flex items-center flex-wrap gap-2 mb-6">
        <span className="text-sm text-gray-500">Trending Searches:</span>
        {HOT_KEYWORDS.map((kw) => (
          <button
            key={kw}
            onClick={() => handleHotSearch(kw)}
            className="px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            {kw}
          </button>
        ))}
      </div>

      {searchQuery && (
        <SearchResults
          results={searchResults}
          isLoading={isLoading}
          query={searchQuery}
          country={currentCountry}
          onDownload={(icon) => setDownloadIcon(icon)}
        />
      )}

      {downloadIcon && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-xl shadow-xl">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-gray-50 overflow-hidden flex items-center justify-center">
                  <img src={downloadIcon.artworkUrl512} alt={downloadIcon.trackName} className="w-full h-full object-contain" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Download Icon</div>
                  <div className="font-semibold text-gray-900">{downloadIcon.trackName}</div>
                </div>
              </div>
              <button onClick={() => setDownloadIcon(null)} className="p-2 rounded hover:bg-gray-100">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">Quick Select</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <button type="button" onClick={applyQuickPNGAll} className="text-left p-4 border border-gray-300 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-colors">
                    <div className="text-base font-semibold text-gray-900 mb-1">PNG All Sizes</div>
                    <div className="text-xs text-gray-500">16px - 512px (6 files)</div>
                  </button>
                  <button type="button" onClick={applyQuickICOCommon} className="text-left p-4 border border-gray-300 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-colors">
                    <div className="text-base font-semibold text-gray-900 mb-1">ICO Icons</div>
                    <div className="text-xs text-gray-500">16px - 64px (3 files)</div>
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-700">Custom Options</div>
                <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="text-sm text-gray-700">{showAdvanced ? 'Hide Advanced Options' : 'Show Advanced Options'}</button>
              </div>

              {options.map((opt, idx) => (
                <div key={idx} className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm text-gray-600">Option {idx + 1}</div>
                    {options.length > 1 && (
                      <button type="button" onClick={() => removeOption(idx)} className="p-1 rounded hover:bg-gray-100">
                        <X className="w-4 h-4 text-gray-500" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Format</label>
                      <select value={opt.format} onChange={(e) => updateOptionFormat(idx, e.target.value as any)} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                        <option value="png">PNG - lossless, supports transparency</option>
                        <option value="jpeg">JPEG - lossy, smaller file size</option>
                        <option value="webp">WebP - modern, high compression</option>
                        <option value="ico">ICO - Windows icon format</option>
                        <option value="svg">SVG - vector, infinite scaling</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Size</label>
                      <select value={opt.size} onChange={(e) => updateOptionSize(idx, parseInt(e.target.value, 10))} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                        {SIZE_CHOICES.map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}

              <button type="button" onClick={addOption} className="w-full px-3 py-2 rounded-lg border border-dashed border-gray-300 text-gray-700">Add More Options</button>

              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm text-gray-600 mb-2">Download Preview</div>
                <ul className="text-sm text-gray-700 space-y-1">
                  {options.map((opt, idx) => (
                    <li key={idx} className="flex justify-between">
                      <span>{downloadIcon.trackName}-{opt.size}x{opt.size}.{opt.format === 'jpeg' ? 'jpg' : opt.format}</span>
                      <span className="text-gray-500">{String(opt.format).toUpperCase()} {opt.size}×{opt.size}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="text-sm text-gray-700">{showAdvanced ? 'Hide Advanced Options' : 'Show Advanced Options'}</button>
                {showAdvanced && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Quality</label>
                    <input type="range" min="0.1" max="1" step="0.1" value={quality} onChange={(e) => setQuality(parseFloat(e.target.value))} className="w-full" />
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between p-4 border-t">
              <div className="text-sm text-gray-600">Will download {options.length} files</div>
              <div className="space-x-2">
                <button onClick={() => setDownloadIcon(null)} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700">Cancel</button>
                <button onClick={startDownload} className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700">Start Download</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
